import {
  CANONICAL_TREE_VIEWER_TERMINAL_FAILURE_KEYS,
  type CanonicalTreeConnectorRoot,
  type CanonicalTreeConnectorRouteStatus,
  type CanonicalTreeViewerTerminalEligibilityEvidence,
  type CanonicalTreeViewerTerminalFailureKey,
} from '../tree/CanonicalTreeConnectorCorridorAuthority';

import type {
  TreeGeometryEvidenceIssue,
} from '../treeGeometryEvidence';

export const TREE_RUNTIME_LEGACY_ANCESTRY_CAUSAL_SCHEMA =
  'CC-TREE-LIVE-003G3E2' as const;

export const LEGACY_ANCESTRY_HORIZONTAL_RULES =
  Object.freeze([
    'TREE-RULE-CHILD-WITHIN-PARENT-CLUSTER',
    'TREE-RULE-COPARENT-SHARED-CHILD',
    'TREE-RULE-BRANCH-NONINTERLEAVING',
    'TREE-RULE-SIBLING-CLUSTER-CONTIGUOUS',
  ] as const);

export type LegacyAncestryHorizontalRule =
  typeof LEGACY_ANCESTRY_HORIZONTAL_RULES[number];

export interface LegacyAncestryCausalEdge {
  readonly key: string;
  readonly fromMemberIds: readonly string[];
  readonly toMemberId: string;
  readonly routeRoot:
    CanonicalTreeConnectorRoot;

  readonly routeStatus:
    CanonicalTreeConnectorRouteStatus;

  readonly viewerTerminalEligibility:
    CanonicalTreeViewerTerminalEligibilityEvidence | null;
}

export interface LegacyAncestryAuthorityEvidence {
  readonly branch: {
    readonly source:
      'canonical' | 'legacy-fallback';
    readonly legacyBranchPathByMemberId:
      ReadonlyMap<string, string>;
    readonly canonicalBranchPathByMemberId:
      ReadonlyMap<string, string>;
  };

  readonly layoutPath: {
    readonly source:
      'canonical-compatible' | 'legacy-fallback';
    readonly legacyPathByMemberId:
      ReadonlyMap<string, string>;
    readonly canonicalCompatiblePathByMemberId:
      ReadonlyMap<string, string>;
  };
}

export interface LegacyAncestryRuleLocalization {
  readonly violationCount: number;
  readonly branchDivergentCount: number;
  readonly layoutDivergentCount: number;
  readonly anyDivergentCount: number;
  readonly indeterminateCount: number;
  readonly parityCount: number;
}

export interface LegacyAncestryCausalLocalization {
  readonly evidenceStatus:
    'OBSERVED' | 'INVALID_EVIDENCE';

  readonly branchLegacyFallbackSelected: boolean;
  readonly layoutLegacyFallbackSelected: boolean;

  readonly rootBoundaryRejectedCount: number;

  readonly rootBoundaryMaternalCount: number;
  readonly rootBoundaryPaternalCount: number;
  readonly rootBoundaryOtherRootCount: number;

  readonly rootBoundaryViewerTerminalCandidateCount: number;
  readonly rootBoundaryOrdinaryCount: number;
  readonly rootBoundaryCandidateIneligibleCount: number;
  readonly rootBoundaryEligibleContradictionCount: number;

  readonly viewerTerminalFailureReasonCounts:
    Readonly<
      Record<
        CanonicalTreeViewerTerminalFailureKey,
        number
      >
    >;

  readonly rootBoundaryBranchDivergentCount: number;
  readonly rootBoundaryLayoutDivergentCount: number;
  readonly rootBoundaryAnyDivergentCount: number;
  readonly rootBoundaryIndeterminateCount: number;
  readonly rootBoundaryParityCount: number;

  readonly horizontal:
    Readonly<
      Record<
        LegacyAncestryHorizontalRule,
        LegacyAncestryRuleLocalization
      >
    >;

  readonly invalidJoinCount: number;

  readonly upstreamAuthorityProven: true;
  readonly overlapIsCausation: false;
  readonly causalAttributionAllowed: false;
  readonly sourceMutationAllowed: false;
  readonly readOnly: true;
  readonly repairExecuted: false;
  readonly privateIdentityPublished: false;
}

type Disposition =
  | 'PARITY'
  | 'DIVERGENT'
  | 'UNAVAILABLE';

const disposition = (
  memberId: string,
  legacy: ReadonlyMap<string, string>,
  canonical: ReadonlyMap<string, string>,
): Disposition => {
  const oldValue =
    legacy.get(memberId);

  const newValue =
    canonical.get(memberId);

  if (
    oldValue === undefined &&
    newValue === undefined
  ) {
    return 'UNAVAILABLE';
  }

  if (
    oldValue === undefined ||
    newValue === undefined
  ) {
    return 'DIVERGENT';
  }

  return oldValue === newValue
    ? 'PARITY'
    : 'DIVERGENT';
};

const cohort = (
  memberIds: readonly string[],
  evidence: LegacyAncestryAuthorityEvidence,
) => {
  const ids =
    Array.from(new Set(memberIds));

  if (ids.length === 0) {
    return {
      branchDivergent: false,
      layoutDivergent: false,
      indeterminate: true,
    };
  }

  const branch =
    ids.map(
      id =>
        disposition(
          id,
          evidence.branch
            .legacyBranchPathByMemberId,
          evidence.branch
            .canonicalBranchPathByMemberId,
        ),
    );

  const layout =
    ids.map(
      id =>
        disposition(
          id,
          evidence.layoutPath
            .legacyPathByMemberId,
          evidence.layoutPath
            .canonicalCompatiblePathByMemberId,
        ),
    );

  return {
    branchDivergent:
      branch.includes('DIVERGENT'),

    layoutDivergent:
      layout.includes('DIVERGENT'),

    indeterminate:
      branch.includes('UNAVAILABLE') ||
      layout.includes('UNAVAILABLE'),
  };
};

const emptyRule = (
): LegacyAncestryRuleLocalization => ({
  violationCount: 0,
  branchDivergentCount: 0,
  layoutDivergentCount: 0,
  anyDivergentCount: 0,
  indeterminateCount: 0,
  parityCount: 0,
});

export const classifyLegacyAncestryResidueCausality =
(
  edges:
    readonly LegacyAncestryCausalEdge[],

  issues:
    readonly TreeGeometryEvidenceIssue[],

  authority:
    LegacyAncestryAuthorityEvidence,
): LegacyAncestryCausalLocalization => {
  const horizontal =
    Object.fromEntries(
      LEGACY_ANCESTRY_HORIZONTAL_RULES.map(
        rule => [
          rule,
          emptyRule(),
        ],
      ),
    ) as Record<
      LegacyAncestryHorizontalRule,
      LegacyAncestryRuleLocalization
    >;

  const edgeByKey =
    new Map<
      string,
      LegacyAncestryCausalEdge
    >();

  let invalidJoinCount = 0;

  for (const edge of edges) {
    if (edgeByKey.has(edge.key)) {
      invalidJoinCount += 1;
      continue;
    }

    edgeByKey.set(edge.key, edge);
  }

  const rejected =
    edges.filter(
      edge =>
        edge.routeStatus ===
        'REJECTED-ROOT-BOUNDARY',
    );

  const rejectedIssueKeys =
    new Set<string>();

  for (const issue of issues) {
    if (
      issue.status !== 'FAIL' ||
      issue.rule !==
        'GEOMETRY-CONNECTOR-ROUTE-REJECTED' ||
      issue.routeRejectionStatus !==
        'REJECTED-ROOT-BOUNDARY'
    ) {
      continue;
    }

    if (
      !issue.edgeKeys ||
      issue.edgeKeys.length !== 1
    ) {
      invalidJoinCount += 1;
      continue;
    }

    rejectedIssueKeys.add(
      issue.edgeKeys[0],
    );
  }

  for (const edge of rejected) {
    if (!rejectedIssueKeys.has(edge.key)) {
      invalidJoinCount += 1;
    }
  }

  for (const key of rejectedIssueKeys) {
    if (
      edgeByKey.get(key)?.routeStatus !==
      'REJECTED-ROOT-BOUNDARY'
    ) {
      invalidJoinCount += 1;
    }
  }

  let rootBoundaryMaternalCount = 0;
  let rootBoundaryPaternalCount = 0;
  let rootBoundaryOtherRootCount = 0;

  let rootBoundaryViewerTerminalCandidateCount = 0;
  let rootBoundaryOrdinaryCount = 0;
  let rootBoundaryCandidateIneligibleCount = 0;
  let rootBoundaryEligibleContradictionCount = 0;

  const viewerTerminalFailureReasonCounts =
    Object.fromEntries(
      CANONICAL_TREE_VIEWER_TERMINAL_FAILURE_KEYS.map(
        key => [key, 0],
      ),
    ) as Record<
      CanonicalTreeViewerTerminalFailureKey,
      number
    >;

  let rootBoundaryBranchDivergentCount = 0;
  let rootBoundaryLayoutDivergentCount = 0;
  let rootBoundaryAnyDivergentCount = 0;
  let rootBoundaryIndeterminateCount = 0;
  let rootBoundaryParityCount = 0;

  for (const edge of rejected) {
    if (edge.routeRoot === 'maternal') {
      rootBoundaryMaternalCount += 1;
    } else if (edge.routeRoot === 'paternal') {
      rootBoundaryPaternalCount += 1;
    } else {
      rootBoundaryOtherRootCount += 1;
      invalidJoinCount += 1;
    }

    const terminal =
      edge.viewerTerminalEligibility;

    if (terminal === null) {
      invalidJoinCount += 1;
    } else {
      if (terminal.candidate) {
        rootBoundaryViewerTerminalCandidateCount += 1;

        if (!terminal.eligible) {
          rootBoundaryCandidateIneligibleCount += 1;
        }
      } else {
        rootBoundaryOrdinaryCount += 1;
      }

      /*
       * The corridor cannot legitimately return
       * REJECTED-ROOT-BOUNDARY after the existing
       * viewer-terminal predicate succeeds.
       */
      if (terminal.eligible) {
        rootBoundaryEligibleContradictionCount += 1;
        invalidJoinCount += 1;
      }

      for (const reason of terminal.failureReasons) {
        if (
          Object.prototype.hasOwnProperty.call(
            viewerTerminalFailureReasonCounts,
            reason,
          )
        ) {
          viewerTerminalFailureReasonCounts[
            reason
          ] += 1;
        } else {
          invalidJoinCount += 1;
        }
      }
    }

    const state =
      cohort(
        [
          ...edge.fromMemberIds,
          edge.toMemberId,
        ],
        authority,
      );

    if (state.branchDivergent) {
      rootBoundaryBranchDivergentCount += 1;
    }

    if (state.layoutDivergent) {
      rootBoundaryLayoutDivergentCount += 1;
    }

    if (
      state.branchDivergent ||
      state.layoutDivergent
    ) {
      rootBoundaryAnyDivergentCount += 1;
    }

    if (state.indeterminate) {
      rootBoundaryIndeterminateCount += 1;
    }

    if (
      !state.branchDivergent &&
      !state.layoutDivergent &&
      !state.indeterminate
    ) {
      rootBoundaryParityCount += 1;
    }
  }

  for (const issue of issues) {
    if (
      !LEGACY_ANCESTRY_HORIZONTAL_RULES
        .includes(
          issue.rule as
            LegacyAncestryHorizontalRule,
        )
    ) {
      continue;
    }

    const rule =
      issue.rule as
        LegacyAncestryHorizontalRule;

    const current =
      horizontal[rule];

    const state =
      cohort(
        issue.memberIds ?? [],
        authority,
      );

    horizontal[rule] = {
      violationCount:
        current.violationCount + 1,

      branchDivergentCount:
        current.branchDivergentCount +
        (state.branchDivergent ? 1 : 0),

      layoutDivergentCount:
        current.layoutDivergentCount +
        (state.layoutDivergent ? 1 : 0),

      anyDivergentCount:
        current.anyDivergentCount +
        (
          state.branchDivergent ||
          state.layoutDivergent
            ? 1
            : 0
        ),

      indeterminateCount:
        current.indeterminateCount +
        (state.indeterminate ? 1 : 0),

      parityCount:
        current.parityCount +
        (
          !state.branchDivergent &&
          !state.layoutDivergent &&
          !state.indeterminate
            ? 1
            : 0
        ),
    };
  }

  return Object.freeze({
    evidenceStatus:
      invalidJoinCount === 0
        ? 'OBSERVED'
        : 'INVALID_EVIDENCE',

    branchLegacyFallbackSelected:
      authority.branch.source ===
      'legacy-fallback',

    layoutLegacyFallbackSelected:
      authority.layoutPath.source ===
      'legacy-fallback',

    rootBoundaryRejectedCount:
      rejected.length,

    rootBoundaryMaternalCount,
    rootBoundaryPaternalCount,
    rootBoundaryOtherRootCount,

    rootBoundaryViewerTerminalCandidateCount,
    rootBoundaryOrdinaryCount,
    rootBoundaryCandidateIneligibleCount,
    rootBoundaryEligibleContradictionCount,

    viewerTerminalFailureReasonCounts:
      Object.freeze({
        ...viewerTerminalFailureReasonCounts,
      }),

    rootBoundaryBranchDivergentCount,
    rootBoundaryLayoutDivergentCount,
    rootBoundaryAnyDivergentCount,
    rootBoundaryIndeterminateCount,
    rootBoundaryParityCount,

    horizontal:
      Object.freeze(horizontal),

    invalidJoinCount,

    upstreamAuthorityProven:
      true,

    overlapIsCausation:
      false,

    causalAttributionAllowed:
      false,

    sourceMutationAllowed:
      false,

    readOnly:
      true,

    repairExecuted:
      false,

    privateIdentityPublished:
      false,
  });
};
